import React, { useState, useCallback, FormEvent, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import { useNotification } from "../Notification/NotificationProvider";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { useCurrentUserInfo } from "../../hooks/useCurrentUserInfo";
import { useTariffAccess } from "../../hooks/useTariffAccess";
import { getRemainingCheckCount } from "../../utils/tariffAccess";
import { CreateRequestUrlList } from "./CreateRequestUrlList";
import { validateCreateRequestForm } from "./createRequestForm";
import { createRequestsSequentially, getBatchCreateNotification } from "./createRequestsBatch";
import "./CreateRequestModal.scss";

export interface CreateRequestModalResult {
  ids: string[];
  openScheduleSetup?: boolean;
}

interface CreateRequestModalProps {
  onClose: () => void;
  onCreated: (result: CreateRequestModalResult) => void;
}

export const CreateRequestModal: React.FC<CreateRequestModalProps> = ({
  onClose,
  onCreated,
}) => {
  const { showNotification } = useNotification();
  const { activeTariffs, userLimits } = useCurrentUserInfo();
  const tariffAccess = useTariffAccess();
  const remainingChecks = useMemo(
    () => getRemainingCheckCount(activeTariffs, userLimits),
    [activeTariffs, userLimits]
  );
  const [companyName, setCompanyName] = useState("");
  const [inn, setInn] = useState("");
  const [urls, setUrls] = useState<string[]>([""]);
  const [periodicity, setPeriodicity] = useState<"once" | "periodic">("once");
  const periodicityOptions = useMemo(
    () =>
      tariffAccess.canScanBySchedule
        ? ([
            { value: "once" as const, label: "Разово" },
            { value: "periodic" as const, label: "По расписанию" },
          ] as const)
        : ([{ value: "once" as const, label: "Разово" }] as const),
    [tariffAccess.canScanBySchedule]
  );
  const [companyNameError, setCompanyNameError] = useState<string | null>(null);
  const [innError, setInnError] = useState<string | null>(null);
  const [urlErrors, setUrlErrors] = useState<(string | null)[]>([null]);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canAddUrl =
    remainingChecks == null || urls.length < remainingChecks;

  const resetErrors = useCallback(() => {
    setGeneralError(null);
  }, []);

  useEffect(() => {
    if (!tariffAccess.canScanBySchedule && periodicity === "periodic") {
      setPeriodicity("once");
    }
  }, [periodicity, tariffAccess.canScanBySchedule]);

  const handleUrlChange = useCallback((index: number, value: string) => {
    setUrls((current) => current.map((url, i) => (i === index ? value : url)));
    setUrlErrors((current) => current.map((error, i) => (i === index ? null : error)));
    resetErrors();
  }, [resetErrors]);

  const handleUrlAdd = useCallback(() => {
    if (!canAddUrl) return;
    setUrls((current) => [...current, ""]);
    setUrlErrors((current) => [...current, null]);
    resetErrors();
  }, [canAddUrl, resetErrors]);

  const handleUrlRemove = useCallback((index: number) => {
    setUrls((current) => current.filter((_, i) => i !== index));
    setUrlErrors((current) => current.filter((_, i) => i !== index));
    resetErrors();
  }, [resetErrors]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const validation = validateCreateRequestForm(companyName, inn, urls, remainingChecks);
      setCompanyNameError(validation.companyNameError);
      setInnError(validation.innError);
      setUrlErrors(validation.urlErrors);
      setGeneralError(validation.generalError);

      if (!validation.isValid) return;

      setSubmitting(true);

      const payloads = validation.filledUrls.map((url) => ({
        companyName: companyName.trim(),
        inn: inn.trim(),
        url,
        status: "new" as const,
      }));

      createRequestsSequentially(payloads)
        .then((result) => {
          const notification = getBatchCreateNotification(result, payloads.length);
          if (notification) showNotification(notification);

          if (result.succeeded.length) {
            onCreated({
              ids: result.succeeded.map((item) => item.id),
              openScheduleSetup:
                tariffAccess.canScanBySchedule &&
                periodicity === "periodic" &&
                result.succeeded.length === 1,
            });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [companyName, inn, urls, remainingChecks, onCreated, periodicity, showNotification, tariffAccess.canScanBySchedule]
  );

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div className="detail-checks-modal" onClick={(e) => e.stopPropagation()}>
        <div className="detail-checks-modal-head">
          <h3>Создать заявку</h3>
          <button
            type="button"
            className="detail-tasklist-close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <X size={18} />
          </button>
        </div>

        <form
          id="create-request-modal-form"
          className="detail-checks-modal-body create-request-modal-form"
          onSubmit={handleSubmit}
        >
          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label">Наименование компании</label>
            <input
              type="text"
              className={`create-request-modal-input${companyNameError ? " create-request-modal-input--error" : ""}`}
              placeholder="Введите наименование"
              value={companyName}
              autoFocus
              onChange={(e) => {
                setCompanyName(e.target.value);
                setCompanyNameError(null);
                resetErrors();
              }}
            />
            {companyNameError && (
              <span className="create-request-modal-field-error">{companyNameError}</span>
            )}
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label">ИНН</label>
            <input
              type="text"
              className={`create-request-modal-input${innError ? " create-request-modal-input--error" : ""}`}
              placeholder="Введите ИНН"
              value={inn}
              maxLength={12}
              onChange={(e) => {
                setInn(e.target.value.replace(/\D/g, ""));
                setInnError(null);
                resetErrors();
              }}
            />
            {innError && <span className="create-request-modal-field-error">{innError}</span>}
          </div>

          <CreateRequestUrlList
            urls={urls}
            urlErrors={urlErrors}
            canAdd={canAddUrl}
            onChange={handleUrlChange}
            onAdd={handleUrlAdd}
            onRemove={handleUrlRemove}
          />

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label">Периодичность проверок</label>
            <FormDropdown
              value={periodicity}
              options={[...periodicityOptions]}
              onChange={setPeriodicity}
            />
          </div>

          {generalError && (
            <span className="create-request-modal-general-error">{generalError}</span>
          )}
        </form>

        <div className="detail-checks-modal-footer">
          <button
            type="submit"
            form="create-request-modal-form"
            className="create-request-modal-submit"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size="sm" /> : "Создать"}
          </button>
        </div>
        </div>
      </div>
    </ModalPortal>
  );
};
